import { addYears, isBefore, addDays, addMonths, addWeeks, subDays, min, max } from 'date-fns'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { normalizeToUTCMidnight } from '@omnea-app/packages/universal/date-utils/normalizeToUTCMidnight'
import { parseDateString } from '@omnea-app/packages/universal/date-utils/parseDateString'
import { toUTCDateMidnight } from '@omnea-app/packages/universal/date-utils/toUTCDateMidnight'
import { utcToLocal } from '@omnea-app/packages/universal/date-utils/utcToLocal'
import { Frequency, type PricingFormAnswerValue, type PricingRowValue } from '@omnea-app/packages/universal/form/elements/pricing'
import { PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { addIntervals } from './totalAmount/addIntervals'
import { countCompleteIntervals } from './totalAmount/countCompleteIntervals'

const normalizeOptionalDate = (date: Date | undefined): Date | undefined => (date ? normalizeToUTCMidnight(date) : undefined)

export const calculateYearCost = ({
  priceRows,
  termStartDate,
  termEndDate,
}: {
  priceRows: PricingRowValue[]
  termStartDate?: Date
  termEndDate?: Date
}): number => {
  if (isEmpty(priceRows)) {
    return 0
  }
  const allStartDates = compact(priceRows.map((item) => parseDateString(item.startDate)))
  const minimumStartDate = !isEmpty(allStartDates) ? min(allStartDates) : null
  // We should somehow find a start date for the date range
  // Ideally it should be the contract period start date if it is set, otherwise it should be the minimum start date of the pricing rows
  const startDate = termStartDate ?? minimumStartDate ?? new Date()

  return priceRows.reduce((total: number, item: PricingRowValue) => {
    const yearOneCost = calculateYearOneCostForEachPricingRow({
      priceRow: item,
      termStartDate: startDate,
      termEndDate,
    })
    return total + yearOneCost
  }, 0)
}

const calculateYearOneCostForEachPricingRow = ({
  priceRow,
  termStartDate,
  termEndDate,
}: {
  priceRow: PricingRowValue
  termStartDate: Date
  termEndDate?: Date
}): number => {
  const rowStartDate = normalizeOptionalDate(parseDateString(priceRow.startDate))
  const rowEndDate = normalizeOptionalDate(parseDateString(priceRow.endDate))
  const normalizedTermStartDate = normalizeToUTCMidnight(termStartDate)
  const normalizedTermEndDate = normalizeOptionalDate(termEndDate)
  const startDate = max(compact([rowStartDate, normalizedTermStartDate]))
  // Use inclusive end date: last day of the first year (e.g. Jan 1 2025 → Dec 31 2025)
  // Convert to local midnight for date-fns arithmetic, then back to UTC midnight
  const lastDayOfFirstYear = toUTCDateMidnight(subDays(addYears(utcToLocal(normalizedTermStartDate), 1), 1))
  const endDate = min(compact([lastDayOfFirstYear, normalizedTermEndDate, rowEndDate]))
  const quantity = countBillingIntervals(startDate, endDate, priceRow.frequency)
  return Math.max(quantity, 0) * (priceRow.amount ?? 0)
}

export const calculateTotalCost = ({
  priceRows,
  termStartDate,
  termEndDate,
}: {
  priceRows: Pick<PricingRowValue, 'amount' | 'frequency' | 'startDate' | 'endDate'>[]
  termStartDate?: Date
  termEndDate?: Date
}): number | null => {
  if (isEmpty(priceRows)) return 0

  const allCosts = priceRows.map((priceRow) => {
    const normalizedTermStartDate = normalizeOptionalDate(termStartDate)
    const normalizedTermEndDate = normalizeOptionalDate(termEndDate)
    const normalizedRowStartDate = normalizeOptionalDate(parseDateString(priceRow.startDate))
    const normalizedRowEndDate = normalizeOptionalDate(parseDateString(priceRow.endDate))
    const endDate = normalizedRowEndDate ?? normalizedTermEndDate
    if (isNil(endDate) && priceRow.frequency !== PaymentIntervalEnum.ONE_OFF) {
      return null
    }
    const definedStartDate = normalizedRowStartDate ?? normalizedTermStartDate ?? normalizeToUTCMidnight(new Date())
    const definedEndDate = endDate ?? definedStartDate
    const priceRowStartDate = max(compact([normalizedRowStartDate, definedStartDate]))
    const priceRowEndDate = min(compact([normalizedRowEndDate, definedEndDate]))
    const quantity = countBillingIntervals(priceRowStartDate, priceRowEndDate, priceRow.frequency)
    return Math.max(quantity, 0) * (priceRow.amount ?? 0)
  })
  // If any of the costs can't be calculated, return null for the total cost
  if (allCosts.some((cost) => cost === null)) {
    return null
  }
  return compact(allCosts).reduce((total, cost) => total + cost, 0)
}

/**
 * Derives a pricing answer's aggregate costs from its priceRows. `totalCost` falls back to
 * `yearOneCost` when it can't be computed (recurring rows with no end date). This is the single
 * source of truth for turning priceRows into stored totals — reused by the form viewer, AI/CSV/ext-op
 * prefill, and backend request-value derivation so the rule stays consistent everywhere.
 */
export const calculatePricingTotals = ({
  priceRows,
  termStartDate,
  termEndDate,
}: {
  priceRows: PricingRowValue[]
  termStartDate?: Date
  termEndDate?: Date
}): { yearOneCost: number; totalCost: number } => {
  const yearOneCost = calculateYearCost({ priceRows, termStartDate, termEndDate })
  const totalCost = calculateTotalCost({ priceRows, termStartDate, termEndDate })
  return { yearOneCost, totalCost: totalCost ?? yearOneCost }
}

/** Total for a unit-priced row (quantity × unit price), rounded to 2dp. Nullish inputs count as 0. */
export const calculatePricingRowAmount = ({
  amountPerUnit,
  quantity,
}: {
  amountPerUnit: number | null | undefined
  quantity: number | null | undefined
}): number => Math.round((quantity ?? 0) * (amountPerUnit ?? 0) * 100) / 100

const addDurationForFrequency = (date: Date, duration: number, frequency: Frequency): Date | null => {
  switch (frequency) {
    case PaymentIntervalEnum.ONE_OFF:
      return null
    case PaymentIntervalEnum.WEEK:
      return addWeeks(date, duration)
    case PaymentIntervalEnum.MONTH:
      return addMonths(date, duration)
    case PaymentIntervalEnum.QUARTER:
      return addMonths(date, duration * 3)
    case PaymentIntervalEnum.EVERY_SIX_MONTHS:
      return addMonths(date, duration * 6)
    case PaymentIntervalEnum.YEAR:
      return addYears(date, duration)
    case PaymentIntervalEnum.EVERY_TWO_YEARS:
      return addYears(date, duration * 2)
    default:
      assertNever(frequency)
      return null
  }
}

/**
 * The inclusive end date implied by a recurring row's start date + duration (e.g. start 1 Jan 2025,
 * 12 monthly periods → 31 Dec 2025). Returns null for one-off rows or when there is nothing to derive.
 */
export const calculatePricingRowEndDate = (
  startDate: string | Date | null,
  duration: number | undefined,
  frequency: Frequency
): Date | null => {
  const start = parseDateString(startDate)
  if (!start || frequency === PaymentIntervalEnum.ONE_OFF || !duration || duration <= 0) {
    return null
  }

  // Work on a local-time representation of the UTC date so date-fns (local-time internally) operates on
  // the correct calendar date; otherwise a UTC-midnight date shifts to the previous day in western zones.
  const localDate = new Date(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  const end = addDurationForFrequency(localDate, Number(duration), frequency)
  if (!end) return null

  // The end date is the last day of the period (e.g. start 1/1/2025, 1 year → 31/12/2025).
  end.setDate(end.getDate() - 1)
  return new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()))
}

/**
 * Fills in a row's derivable fields before its totals are computed: `amount` from `amountPerUnit × quantity`,
 * and the recurring `endDate` from `duration`. Used by producers that build pricing answers outside the form
 * viewer (migration/AI/import backfill) so they reach the same totals the form would compute.
 *
 * Pass `deriveEndDate: false` for a question whose `endDate` subfield is hidden: the viewer nulls a hidden
 * endDate before computing costs, and a derived one bounds `totalCost` to the row's own period instead of
 * the contract term (calculateTotalCost prefers a row endDate over termEndDate).
 */
export const normalisePricingRowForTotals = (
  row: PricingRowValue,
  { deriveEndDate = true }: { deriveEndDate?: boolean } = {}
): PricingRowValue => ({
  ...row,
  amount: row.amount ?? calculatePricingRowAmount({ amountPerUnit: row.amountPerUnit, quantity: row.quantity }),
  endDate: deriveEndDate
    ? (row.endDate ?? calculatePricingRowEndDate(row.startDate, row.duration ?? undefined, row.frequency))
    : row.endDate,
})

/**
 * Returns a pricing answer value with its `priceRows` normalised and its `yearOneCost`/`totalCost`
 * recomputed from those rows. The canonical way for producers outside the form viewer (migration/AI/
 * import backfill, request-value derivation) to populate a pricing answer's totals consistently.
 */
export const derivePricingAnswerTotals = (
  pricingValue: PricingFormAnswerValue,
  { termStartDate, termEndDate }: { termStartDate?: Date; termEndDate?: Date } = {},
  { deriveEndDate }: { deriveEndDate?: boolean } = {}
): PricingFormAnswerValue => {
  const priceRows = pricingValue.priceRows.map((row) => normalisePricingRowForTotals(row, { deriveEndDate }))
  const { yearOneCost, totalCost } = calculatePricingTotals({ priceRows, termStartDate, termEndDate })
  return { ...pricingValue, priceRows, yearOneCost, totalCost }
}

/**
 * Counts the number of billing intervals for an inclusive date range.
 * Partial periods are rounded up because billing occurs at the start of each period.
 *
 * "Inclusive" means the end date is the last day of the period.
 * e.g., Jan 1 2025 → Dec 31 2025 = exactly 1 year = 1 interval
 * e.g., Jan 1 2025 → Jan 1 2026 = 1 year + 1 day = 2 intervals (2nd year has started)
 */
const countBillingIntervals = (startDate: Date, endDate: Date, frequency: Frequency): number => {
  // First normalize to UTC midnight to get the correct calendar date,
  // then convert to local midnight so date-fns (which uses local time internally)
  // sees consistent time-of-day across DST boundaries.
  const localStart = utcToLocal(normalizeToUTCMidnight(startDate))
  const localEnd = utcToLocal(normalizeToUTCMidnight(endDate))

  // Convert inclusive end to exclusive end (+1 day) because end dates in the system
  // represent the last day of the period (e.g. 01/01/2025 → 31/12/2025)
  const exclusiveEnd = addDays(localEnd, 1)

  // If there is no overlap at all, or the range is inverted,
  // treat it as zero units instead of letting date-fns return negative values
  if (!isBefore(localStart, exclusiveEnd)) return 0

  if (frequency === PaymentIntervalEnum.ONE_OFF) return 1

  const complete = countCompleteIntervals(frequency, localStart, exclusiveEnd)
  const boundary = addIntervals(frequency, localStart, complete)

  // If the exclusive end date extends past the last complete interval boundary,
  // another billing period has started (billing occurs at the start of each period)
  const hasStartedNextInterval = exclusiveEnd > boundary

  return complete + (hasStartedNextInterval ? 1 : 0)
}

/**
 * Returns metadata about the billing intervals for a pricing row.
 * Used by the frontend to display period info and suggestions.
 */
export const getBillingPeriodInfo = ({
  startDate,
  endDate,
  frequency,
}: {
  startDate: Date
  endDate: Date
  frequency: Frequency
}): { intervals: number; isPartial: boolean; suggestedEndDate: Date | null } => {
  const localStart = utcToLocal(normalizeToUTCMidnight(startDate))
  const localEnd = utcToLocal(normalizeToUTCMidnight(endDate))
  const exclusiveEnd = addDays(localEnd, 1)

  if (!isBefore(localStart, exclusiveEnd)) {
    return { intervals: 0, isPartial: false, suggestedEndDate: null }
  }

  if (frequency === PaymentIntervalEnum.ONE_OFF) {
    return { intervals: 1, isPartial: false, suggestedEndDate: null }
  }

  const complete = countCompleteIntervals(frequency, localStart, exclusiveEnd)
  const boundary = addIntervals(frequency, localStart, complete)
  const hasStartedNextInterval = exclusiveEnd > boundary
  const intervals = complete + (hasStartedNextInterval ? 1 : 0)

  if (!hasStartedNextInterval) {
    return { intervals, isPartial: false, suggestedEndDate: null }
  }

  // Calculate suggested end date that would round down to exact periods.
  // The boundary is the start of the next (partial) interval, so subtract 1 day
  // to get the last day of the previous complete interval.
  const suggestedEndDate = toUTCDateMidnight(subDays(boundary, 1))

  return { intervals, isPartial: true, suggestedEndDate }
}
